import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useSettingsContext } from './useSettingsContext.hook';
import { SettingsContextType } from './SettingsContext';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { ChangeEvent, useMemo } from 'react';
import { useMobile } from '@act/utilities/mobile-context';
import { CustomizationThemeDto } from '@act/shared/data-transfer-objects';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const groupThemesByCategory = (themes: CustomizationThemeDto[]) => {
  // Group themes by category
  const grouped = themes.reduce((acc, theme) => {
    const category = theme.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(theme);
    return acc;
  }, {} as Record<string, CustomizationThemeDto[]>);

  // Sort categories alphabetically
  const sortedCategories = Object.keys(grouped).sort();

  // Sort themes within each category alphabetically by name
  sortedCategories.forEach(category => {
    grouped[category].sort((a, b) => a.name.localeCompare(b.name));
  });

  return { grouped, sortedCategories };
};

export const SettingsDrawer = ({ open, onClose }: SettingsDrawerProps) => {
  const { isMobile } = useMobile();
  const {
    themeKey,
    setThemeKey,
    logoThemeId,
    accentThemeId,
    customizationThemes,
    showAnimations,
    updateUserAppSettings,
    isLoadingUserSettings,
  } = useSettingsContext();

  // Group and sort themes
  const { grouped: groupedThemes, sortedCategories } = useMemo(
    () => groupThemesByCategory(customizationThemes),
    [customizationThemes]
  );

  const handleThemeChange = (e: SelectChangeEvent<string>) => {
    setThemeKey(e.target.value as SettingsContextType['themeKey']);
  };

  const handleLogoThemeChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value;
    void updateUserAppSettings({
      logoThemeId: value === '' ? null : Number(value),
    });
  };

  const handleAccentThemeChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value;
    void updateUserAppSettings({
      accentThemeId: value === '' ? null : Number(value),
    });
  };

  const handleShowAnimationsChange = (e: ChangeEvent<HTMLInputElement>) => {
    void updateUserAppSettings({
      showAnimations: e.target.checked,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '30%',
          padding: '24px',
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Typography variant="headlineMedium">Settings</Typography>
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <CloseOutlinedIcon />
        </IconButton>
      </Box>
      <Divider sx={{ margin: '24px 0' }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box>
          <Typography
            component="div"
            variant="titleMedium"
            sx={{ marginBottom: '8px' }}
          >
            Theme (Coming Soon)
          </Typography>
          <Select
            value={themeKey}
            onChange={handleThemeChange}
            fullWidth
            disabled={true}
          >
            <MenuItem value="LIGHT_MODE">Light mode</MenuItem>
            <MenuItem value="DARK_MODE">Dark mode</MenuItem>
            <MenuItem value="COLOR_BLIND_MODE">Colorblind mode</MenuItem>
          </Select>
        </Box>

        <Box>
          <Typography
            component="div"
            variant="titleMedium"
            sx={{ marginBottom: '8px' }}
          >
            Logo Colors
          </Typography>
          <Select
            value={logoThemeId ?? ''}
            onChange={handleLogoThemeChange}
            fullWidth
            disabled={isLoadingUserSettings}
            displayEmpty
          >
            <MenuItem value="">None</MenuItem>
            {sortedCategories.map(category => [
              <ListSubheader key={`category-${category}`}>
                {category}
              </ListSubheader>,
              ...groupedThemes[category].map(theme => (
                <MenuItem key={theme.id} value={theme.id} sx={{ pl: 4 }}>
                  {theme.name}
                </MenuItem>
              )),
            ])}
          </Select>
        </Box>

        <Box>
          <Typography
            component="div"
            variant="titleMedium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Header Accent Colors
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAnimations}
                  onChange={handleShowAnimationsChange}
                  disabled={isLoadingUserSettings}
                />
              }
              label="Show animation"
              sx={{ marginLeft: 'auto' }}
            />
          </Typography>
          <Select
            value={accentThemeId ?? ''}
            onChange={handleAccentThemeChange}
            fullWidth
            disabled={isLoadingUserSettings}
            displayEmpty
          >
            <MenuItem value="">None</MenuItem>
            {sortedCategories.map(category => [
              <ListSubheader key={`category-${category}`}>
                {category}
              </ListSubheader>,
              ...groupedThemes[category].map(theme => (
                <MenuItem key={theme.id} value={theme.id} sx={{ pl: 4 }}>
                  {theme.name}
                </MenuItem>
              )),
            ])}
          </Select>
        </Box>
      </Box>
    </Drawer>
  );
};
